import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { QMenuButton, QMenuItem, useFeatureFlag } from "@qualio/ui-components";
import {
  BaseDesignElement,
  DesignElementVersion,
  FeatureFlags,
} from "@design-controls/types";
import { UseQueryResult } from "react-query";
import {
  isNewerDesignElementVersion,
  toDesignElementVersionLabel,
  toDesignElementVersionNumber,
} from "../../domain/designElementVersion";

type Props = {
  designElement: Pick<BaseDesignElement, "version" | "major" | "minor">;
  versions: Pick<
    UseQueryResult<DesignElementVersion[]>,
    "data" | "isLoading" | "isError"
  >;
};

export const DesignElementCompareSelect: React.FC<Props> = ({
  designElement,
  versions,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { selectedItemId } = useParams<{ selectedItemId: string }>();
  const features = useFeatureFlag();
  const compareEnabled = Boolean(
    features[FeatureFlags.VERSION_TRACELINK_UI] &&
      features[FeatureFlags.MAJOR_MINOR_VERSION_UI],
  );

  const items = useMemo(() => versions.data ?? [], [versions.data]);

  const selectable = useMemo(
    () =>
      items.filter((item) => isNewerDesignElementVersion(designElement, item)),
    [items, designElement],
  );

  const viewedLabel = useMemo(() => {
    const viewed = items.find(
      (item) => item.version === designElement.version && !item.isLatest,
    );
    return viewed ? toDesignElementVersionLabel(viewed) : undefined;
  }, [items, designElement.version]);

  const onSelect = useCallback(
    (label: string) => {
      const elementPath = pathname.slice(
        0,
        pathname.indexOf(`/${selectedItemId}`) + `/${selectedItemId}`.length,
      );

      navigate(
        `${elementPath}/compare/${label}` +
          (viewedLabel ? `?version=${viewedLabel}` : ""),
      );
    },
    [navigate, pathname, selectedItemId, viewedLabel],
  );

  if (!compareEnabled) {
    return null;
  }

  return (
    <QMenuButton
      buttonLabel={t("designElementCompare.label")}
      variant="ghost"
      isDisabled={versions.isLoading || versions.isError || !selectable.length}
      data-cy="design-element-compare-select"
    >
      {selectable.map((item) => {
        const label = toDesignElementVersionLabel(item);
        return (
          <QMenuItem
            key={item.version}
            onClick={() => onSelect(label)}
            data-cy={`design-element-compare-${label}`}
          >
            {t("designElementVersions.label", {
              version: toDesignElementVersionNumber(label),
            })}
          </QMenuItem>
        );
      })}
    </QMenuButton>
  );
};
